import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { CheckCircle, Warning } from '@mui/icons-material';
import { useForm, Controller, FormProvider } from 'react-hook-form';

// Base form interface
interface UserRoleForm {
  email: string;
  name: string;
  [key: string]: unknown; // Allow additional fields like role
}

// Types for the dialog component
interface UserExistsResult {
  exists: boolean;
  user?: {
    name?: string;
    email?: string;
  };
}

interface UserExistsHookResult {
  data?: UserExistsResult;
  isLoading: boolean;
}

interface ValidationRule {
  required?: string;
  pattern?: {
    value: RegExp;
    message: string;
  };
  minLength?: {
    value: number;
    message: string;
  };
  maxLength?: {
    value: number;
    message: string;
  };
}

interface AddUserRoleDialogConfig {
  title: string;
  description: string;
  submitLabel: string;
  emailLabel?: string;
  nameLabel?: string;
  nameRequired?: boolean;
  additionalFields?: React.ReactNode;
  onSubmit: (data: UserRoleForm) => Promise<unknown>;
  useUserExistsHook: (email: string, enabled: boolean) => UserExistsHookResult;
}

interface AddUserRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  config: AddUserRoleDialogConfig;
  defaultValues?: Partial<UserRoleForm>;
  isLoading?: boolean;
}

// Simple debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const AddUserRoleDialog: React.FC<AddUserRoleDialogProps> = ({
  open,
  onClose,
  onSuccess,
  config,
  defaultValues = {},
  isLoading: externalLoading = false,
}) => {
  const [emailForCheck, setEmailForCheck] = useState('');
  const [userCheckResult, setUserCheckResult] = useState<{
    exists: boolean;
    message: string;
  } | null>(null);
  const [isTypingValidEmail, setIsTypingValidEmail] = useState(false);
  const processedUserRef = useRef<string | null>(null);

  const debouncedEmail = useDebounce(emailForCheck, 500);

  // Default form values - stabilized to prevent unnecessary resets
  const defaultFormValues: UserRoleForm = useMemo(
    () => ({
      email: '',
      name: '',
      ...defaultValues,
    }),
    // Only recreate when dialog opens, not on every defaultValues change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  // Check if user exists when email changes
  const { data: userExistsData, isLoading: isCheckingUser } = config.useUserExistsHook(
    debouncedEmail,
    !!debouncedEmail && debouncedEmail.includes('@')
  );

  const formMethods = useForm<UserRoleForm>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = formMethods;

  const watchedEmail = watch('email');

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    return !!email && email.includes('@') && email.length > 3;
  };

  // Update email for existence check when form email changes
  useEffect(() => {
    if (watchedEmail) {
      setEmailForCheck(watchedEmail);

      // Set typing state immediately when valid email is detected
      if (isValidEmail(watchedEmail)) {
        setIsTypingValidEmail(true);
      } else {
        setIsTypingValidEmail(false);
      }
    } else {
      setIsTypingValidEmail(false);
    }
  }, [watchedEmail]);

  // Update user check result based on the hook data
  useEffect(() => {
    if (!watchedEmail || !watchedEmail.includes('@')) {
      setUserCheckResult(null);
      processedUserRef.current = null;
      return;
    }

    if (userExistsData) {
      // Create a unique key for this user check to prevent duplicate processing
      const userKey = `${watchedEmail}_${userExistsData.exists}_${userExistsData.user?.name || ''}`;

      // Skip if we've already processed this exact user data
      if (processedUserRef.current === userKey) {
        return;
      }

      processedUserRef.current = userKey;

      // Clear typing state when we get actual results
      setIsTypingValidEmail(false);

      if (userExistsData.exists) {
        setUserCheckResult({
          exists: true,
          message: `User found: ${userExistsData.user?.name || watchedEmail}. Role will be assigned to existing user.`,
        });

        // Auto-populate the name field with existing user's name
        if (userExistsData.user?.name) {
          setValue('name', userExistsData.user.name, { shouldValidate: true });
        }
      } else {
        setUserCheckResult({
          exists: false,
          message: 'User not found. Invitation will be sent to create new account.',
        });

        // Clear name field for non-existing users to ensure fresh start
        setValue('name', '', { shouldValidate: true });
      }
    }
  }, [userExistsData, watchedEmail, setValue]);

  // Track previous open state to only reset when dialog opens (not on every render)
  const prevOpenRef = useRef(false);

  // Reset form when dialog opens (transitions from false to true)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Dialog is opening - reset the form
      reset(defaultFormValues);
      setEmailForCheck('');
      setUserCheckResult(null);
      setIsTypingValidEmail(false);
      processedUserRef.current = null;
    }
    prevOpenRef.current = open;
  }, [open, reset, defaultFormValues]);

  const handleClose = () => {
    reset();
    setEmailForCheck('');
    setUserCheckResult(null);
    setIsTypingValidEmail(false);
    processedUserRef.current = null;
    onClose();
  };

  const onSubmit = async (data: UserRoleForm) => {
    try {
      await config.onSubmit(data);
      handleClose();
      onSuccess?.();
    } catch (error) {
      // Error handling is done in the mutation hooks via notifications
      console.error('Failed to submit user role form:', error);
    }
  };

  const isLoading = externalLoading;
  const userExists = userExistsData?.exists;
  const existingUser = userExistsData?.user;

  // Email validation rules
  const emailValidationRules: ValidationRule = {
    required: 'Email address is required',
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Please enter a valid email address',
    },
  };

  // Name validation rules - dynamic based on user existence and config
  const nameValidationRules: ValidationRule = useMemo(() => {
    const rules: ValidationRule = {
      maxLength: {
        value: 100,
        message: 'Name must not exceed 100 characters',
      },
    };

    // If name is required by config or user doesn't exist, make it required
    if (config.nameRequired || (!userExists && debouncedEmail && debouncedEmail.includes('@'))) {
      rules.required = 'Name is required';
      rules.minLength = {
        value: 2,
        message: 'Name must be at least 2 characters',
      };
    }

    return rules;
  }, [userExists, debouncedEmail, config.nameRequired]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>{config.title}</DialogTitle>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {config.description && (
                <Typography variant="body2" color="text.secondary">
                  {config.description}
                </Typography>
              )}

              {/* User existence check - unified loading and result display */}
              {(isTypingValidEmail || isCheckingUser || userCheckResult) && (
                <Alert
                  severity={
                    isTypingValidEmail || isCheckingUser
                      ? 'info'
                      : userCheckResult?.exists
                        ? 'info'
                        : 'warning'
                  }
                  icon={
                    isTypingValidEmail || isCheckingUser ? (
                      <CircularProgress size={16} />
                    ) : userCheckResult?.exists ? (
                      <CheckCircle />
                    ) : (
                      <Warning />
                    )
                  }
                >
                  {isTypingValidEmail || isCheckingUser
                    ? 'Checking if user exists...'
                    : userCheckResult?.message}
                </Alert>
              )}

              <Controller
                name="email"
                control={control}
                rules={emailValidationRules}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={config.emailLabel || 'Email Address'}
                    variant="outlined"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                    type="email"
                  />
                )}
              />

              {/* Additional fields (e.g., role selector) */}
              {config.additionalFields}

              <Controller
                name="name"
                control={control}
                rules={nameValidationRules}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={config.nameLabel || 'Full Name'}
                    variant="outlined"
                    fullWidth
                    error={!!errors.name}
                    helperText={
                      errors.name?.message ||
                      (userExists && existingUser?.name
                        ? `Using existing user's name: ${existingUser.name}`
                        : config.nameRequired
                          ? 'Required'
                          : 'Optional. If not provided, email will be used as display name.')
                    }
                    disabled={isLoading || (userExists && !!existingUser?.name)}
                    placeholder={
                      userExists && existingUser?.name ? existingUser.name : 'Enter full name...'
                    }
                  />
                )}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} disabled={isLoading} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || isLoading || (isCheckingUser && !userExists)}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
            >
              {isLoading
                ? config.submitLabel.replace(/^(Add|Send|Assign)/, '$1ing...')
                : userExists
                  ? config.submitLabel.replace(/^(Add|Send)/, 'Assign')
                  : config.submitLabel}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddUserRoleDialog;

// Export types for use by consumers
export type {
  UserRoleForm,
  AddUserRoleDialogConfig,
  AddUserRoleDialogProps,
  UserExistsHookResult,
  UserExistsResult,
};
